import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

interface ContestCountdownProps {
  endAt: string; // ISO timestamp
  className?: string;
}

interface TimeRemaining {
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

function calculateTimeRemaining(endAt: string): TimeRemaining {
  const now = new Date().getTime();
  const end = new Date(endAt).getTime();
  const diff = end - now;

  if (diff <= 0) {
    return { hours: 0, minutes: 0, seconds: 0, isExpired: true };
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return { hours, minutes, seconds, isExpired: false };
}

export function ContestCountdown({ endAt, className = '' }: ContestCountdownProps) {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(() =>
    calculateTimeRemaining(endAt)
  );

  useEffect(() => {
    // Update every second
    const interval = setInterval(() => {
      const newTime = calculateTimeRemaining(endAt);
      setTimeRemaining(newTime);

      // Stop updating if expired
      if (newTime.isExpired) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [endAt]);

  if (timeRemaining.isExpired) {
    return (
      <div className={`flex items-center gap-2 text-gray-500 dark:text-white/60 ${className}`}>
        <Clock className="w-4 h-4" />
        <span className="text-sm font-medium">Contest Ended</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <Clock className="w-4 h-4 text-pink-500 animate-pulse" />
      <div className="flex items-center gap-1.5 font-mono text-lg font-bold">
        <div className="flex flex-col items-center">
          <span className="text-2xl text-pink-500 tabular-nums">
            {String(timeRemaining.hours).padStart(2, '0')}
          </span>
          <span className="text-xs text-gray-600 dark:text-white/60 uppercase tracking-wider">
            Hours
          </span>
        </div>
        <span className="text-pink-500 text-xl pb-3">:</span>
        <div className="flex flex-col items-center">
          <span className="text-2xl text-pink-500 tabular-nums">
            {String(timeRemaining.minutes).padStart(2, '0')}
          </span>
          <span className="text-xs text-gray-600 dark:text-white/60 uppercase tracking-wider">
            Mins
          </span>
        </div>
        <span className="text-pink-500 text-xl pb-3">:</span>
        <div className="flex flex-col items-center">
          <span className="text-2xl text-pink-500 tabular-nums">
            {String(timeRemaining.seconds).padStart(2, '0')}
          </span>
          <span className="text-xs text-gray-600 dark:text-white/60 uppercase tracking-wider">
            Secs
          </span>
        </div>
      </div>
    </div>
  );
}

